import { useState, useEffect, useRef } from 'react';
import api, { downloadCsv } from '../utils/api';
import Badge from './Badge';

// Danger Zone → "Move invoices to another PO" (2026-10-15). Every check is
// re-done by the server (utils/poMove.js); this screen only shows them.
const fmt = n => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const box = (bg, color) => ({ background: `var(--${bg})`, color: `var(--${color})`, padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 12 });

function PoCard({ po }) {
  return (
    <div style={{ fontSize: 13, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 6 }}>
      <div style={{ fontWeight: 600 }}>
        {po.po_number} <Badge variant={po.deleted_at ? 'danger' : po.status === 'void' ? 'neutral' : po.status === 'open' ? 'success' : 'info'}>{po.deleted_at ? 'deleted' : po.status}</Badge>
      </div>
      <div style={{ color: 'var(--muted)' }}>
        {po.client_wo_number} · {po.client_name} · {po.vendor_name || '—'} · {po.contractor_name}<br />
        {po.period_start} – {po.period_end} · {po.approved_hours ?? '—'} h · sell {po.sell_rate} / buy {po.buy_rate ?? '—'} · {po.ar_count} AR · {po.ap_count} AP
      </div>
    </div>
  );
}

function PoPicker({ label, po, onPick }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return; }
    const t = setTimeout(() => api.get('/danger-zone/po-move/search', { params: { q } }).then(r => setHits(r.data)).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="form-field" style={{ flex: 1, minWidth: 280 }}>
      <label>{label}</label>
      {po ? (
        <>
          <PoCard po={po} />
          <button className="btn btn-sm btn-ghost" style={{ marginTop: 4 }} onClick={() => { onPick(null); setQ(''); }}>Change</button>
        </>
      ) : (
        <>
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="PO number, e.g. 6011005137" />
          {hits.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 260, overflowY: 'auto' }}>
              {hits.map(h => (
                <div key={h.id} onClick={() => { onPick(h); setHits([]); }} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  <strong>{h.po_number}</strong> <span style={{ color: 'var(--muted)' }}>· {h.status}{h.deleted_at ? ' (deleted)' : ''} · {h.client_wo_number} · {h.contractor_name} · {h.period_start} – {h.period_end} · {h.ar_count} AR / {h.ap_count} AP</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function PoMoveTool() {
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [inv, setInv] = useState(null);
  const [selAr, setSelAr] = useState([]);
  const [selAp, setSelAp] = useState([]);
  const [reason, setReason] = useState('');
  const [voidSource, setVoidSource] = useState(false);
  const voidTouched = useRef(false);
  const [apLinks, setApLinks] = useState({});
  const linksTouched = useRef({});
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  function reset() {
    setInv(null); setSelAr([]); setSelAp([]); setPreview(null); setApLinks({});
    linksTouched.current = {}; voidTouched.current = false; setVoidSource(false);
  }

  useEffect(() => {
    reset();
    if (!source) return;
    api.get(`/danger-zone/po-move/po/${source.id}`).then(r => { setInv(r.data); setSource(s => ({ ...s, ...r.data.po })); }).catch(e => setError(e.response?.data?.error || 'Could not load the source PO'));
  }, [source?.id]);

  const selectable = x => !x.deleted_at && x.status !== 'void';
  const allSelected = inv && [...inv.ar, ...inv.ap].filter(selectable).length === selAr.length + selAp.length;
  // "Set source to void" defaults to ticked when every invoice is being moved.
  useEffect(() => {
    if (!voidTouched.current) setVoidSource(!!inv && allSelected && selAr.length + selAp.length > 0);
  }, [allSelected, selAr.length, selAp.length, inv]);

  const body = { source_po_id: source?.id, target_po_id: target?.id, ar_ids: selAr, ap_ids: selAp, reason, void_source: voidSource, ap_links: apLinks };
  useEffect(() => {
    if (!source || !target || !inv) { setPreview(null); return; }
    const t = setTimeout(() => api.post('/danger-zone/po-move/preview', body).then(r => {
      setPreview(r.data);
      // Pre-select the AR when exactly one on the target matches the AP (period + type).
      const next = {};
      for (const o of r.data.ap_link_options || []) if (o.suggested && !linksTouched.current[o.ap_id] && apLinks[o.ap_id] === undefined) next[o.ap_id] = String(o.suggested);
      if (Object.keys(next).length) setApLinks(l => ({ ...l, ...next }));
    }).catch(e => setError(e.response?.data?.error || 'Preview failed')), 300);
    return () => clearTimeout(t);
  }, [source?.id, target?.id, inv, selAr.join(), selAp.join(), reason, voidSource, JSON.stringify(apLinks)]);

  async function doMove() {
    setBusy(true); setError(null);
    try {
      const r = await api.post('/danger-zone/po-move/execute', { ...body, fingerprint: preview.fingerprint, confirmation: confirmText });
      setResult(r.data); setConfirmOpen(false); setConfirmText('');
      setSource(null); setTarget(null); setReason('');
    } catch (e) {
      setError(e.response?.data?.error || 'Move failed');
      setConfirmOpen(false);
    } finally { setBusy(false); }
  }

  const toggle = (setter, id) => setter(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const canContinue = preview && preview.blocks.length === 0;

  const invRow = (x, kind) => {
    const sel = kind === 'AR' ? selAr : selAp;
    const ok = selectable(x);
    return (
      <tr key={kind + x.id} style={{ opacity: ok ? 1 : 0.55 }}>
        <td><input type="checkbox" disabled={!ok} checked={sel.includes(x.id)} onChange={() => toggle(kind === 'AR' ? setSelAr : setSelAp, x.id)} /></td>
        <td>{kind}</td>
        <td style={{ fontWeight: 500 }}>{x.invoice_number}{x.invoice_type && x.invoice_type !== 'standard' ? ` (${x.invoice_type})` : ''}</td>
        <td>{x.period_start} – {x.period_end}</td>
        <td>{kind === 'AR' ? x.hours : (x.hours_approved ?? x.hours_billed)} h × {x.rate}</td>
        <td style={{ textAlign: 'right' }}>${fmt(kind === 'AR' ? x.amount : x.amount_to_pay)}</td>
        <td>{x.deleted_at ? <Badge variant="danger">deleted</Badge> : x.status}</td>
        <td>{x.pay_count ? `${x.pay_count} · $${fmt(x.pay_total)}` : '—'}</td>
        <td style={{ fontSize: 12, color: 'var(--muted)' }}>{kind === 'AP' && x.linked_ar_number ? `→ AR ${x.linked_ar_number}` : ''}{!ok ? (x.deleted_at ? 'deleted – cannot move' : 'void – cannot move') : ''}</td>
      </tr>
    );
  };

  return (
    <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--danger-text)' }}>Move invoices to another PO</h4>
      <p className="form-hint" style={{ marginBottom: 12 }}>
        For a PO that Hexaware voided and replaced. Moves AR / AP invoices to the new PO in one transaction, with notes, audit rows and a database backup.
      </p>

      {result && (
        <div style={box('success-bg', 'success-text')}>
          <strong>✓ Moved from {result.source_po_number} to {result.target_po_number}.</strong> Backup: <code>{result.backup}</code>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {result.changes.filter(c => c.field !== 'status' || c.old !== c.new).map((c, i) => <li key={i}>{c.label}: {c.field} {c.old_label ?? c.old} → {c.new_label ?? c.new}</li>)}
            {Object.entries(result.readiness || {}).map(([k, v]) => <li key={k}>{k}: AR readiness {v}</li>)}
          </ul>
          {result.moved_ar.map(a => (
            <div key={a.id} style={{ marginTop: 10 }}>
              <button className="btn btn-sm btn-secondary" onClick={() => downloadCsv(`/ar-invoices/${a.id}/pdf`, `${a.invoice_number}.pdf`).catch(e => setError('Could not generate PDF: ' + (e.response?.data?.error || e.message)))}>
                Regenerate PDF – {a.invoice_number}
              </button>
              <div style={{ fontSize: 12, marginTop: 4 }}>Resubmit in P2P with PO <strong>{a.po_number}</strong></div>
            </div>
          ))}
          <div style={{ marginTop: 10 }}><button className="btn btn-sm btn-ghost" onClick={() => setResult(null)}>Done</button></div>
        </div>
      )}
      {error && <div style={box('danger-bg', 'danger-text')}>{error} <button className="btn btn-sm btn-ghost" onClick={() => setError(null)}>×</button></div>}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <PoPicker label="1. Source PO (move from)" po={source} onPick={p => { setError(null); setSource(p); }} />
        <PoPicker label="Target PO (move to)" po={target} onPick={p => { setError(null); setTarget(p); }} />
      </div>

      {inv && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>2. Invoices on {source.po_number}</div>
          {inv.ar.length + inv.ap.length === 0 ? <div className="form-hint" style={{ marginBottom: 12 }}>No invoices on this PO.</div> : (
            <div style={{ overflowX: 'auto', marginBottom: 14 }}>
              <table className="data-table">
                <thead><tr><th></th><th>Type</th><th>Invoice</th><th>Period</th><th>Hours × rate</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th>Payments</th><th></th></tr></thead>
                <tbody>{inv.ar.map(x => invRow(x, 'AR'))}{inv.ap.map(x => invRow(x, 'AP'))}</tbody>
              </table>
            </div>
          )}
        </>
      )}

      {preview && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>3. Checks</div>
          {preview.blocks.length > 0 && <div style={box('danger-bg', 'danger-text')}><strong>Blocked:</strong><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{preview.blocks.map((b, i) => <li key={i}>{b}</li>)}</ul></div>}
          {preview.warnings.length > 0 && <div style={box('warning-bg', 'warning-text')}><strong>Warnings (allowed):</strong><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>}
          {preview.blocks.length === 0 && preview.warnings.length === 0 && <div style={box('success-bg', 'success-text')}>No problems found.</div>}

          {(preview.ap_link_options || []).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Link each moved AP to an AR (optional)</div>
              {preview.ap_link_options.map(o => (
                <div key={o.ap_id} className="form-field" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ minWidth: 160, fontSize: 13 }}>AP {o.ap_number}{o.current_ar_number ? ` (now → ${o.current_ar_number})` : ''}</span>
                  <select value={apLinks[o.ap_id] ?? ''} onChange={e => { linksTouched.current[o.ap_id] = true; setApLinks(l => ({ ...l, [o.ap_id]: e.target.value })); }}>
                    <option value="">— keep current link —</option>
                    {o.options.map(r => <option key={r.id} value={r.id}>{r.invoice_number} · {r.period_start} · ${fmt(r.amount)} · {r.status}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}

          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>Reason (required)</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Hexaware voided the PO and issued a replacement" />
          </div>
          {source.status !== 'void' && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={voidSource} onChange={e => { voidTouched.current = true; setVoidSource(e.target.checked); }} />
              Set source PO {source.po_number} to void
            </label>
          )}

          {(selAr.length + selAp.length) > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>4. Preview of every change</div>
              <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                <table className="data-table">
                  <thead><tr><th>Record</th><th>Field</th><th>Old</th><th>New</th><th>Note appended</th></tr></thead>
                  <tbody>
                    {preview.changes.map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{c.label}</td>
                        <td>{c.field}</td>
                        <td>{c.old_label ?? String(c.old ?? '—')}</td>
                        <td style={{ fontWeight: c.old !== c.new ? 600 : 400 }}>{c.old === c.new ? 'unchanged' : (c.new_label ?? String(c.new))}</td>
                        <td style={{ fontSize: 12, color: 'var(--muted)' }}>{c.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-hint" style={{ marginBottom: 10 }}>A database backup is written just before the change, and an audit row for each change.</div>
              <button className="btn btn-primary" style={{ background: 'var(--danger-text)', opacity: canContinue ? 1 : 0.5 }} disabled={!canContinue || busy} onClick={() => { setConfirmText(''); setConfirmOpen(true); }}>
                Move {selAr.length + selAp.length} invoice(s)…
              </button>
            </>
          )}
        </>
      )}

      {confirmOpen && preview && (
        <div className="panel-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="quick-panel" style={{ width: 440 }} onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title" style={{ color: 'var(--danger-text)' }}>⚠ Move invoices</h3>
              <button className="drawer-close" onClick={() => setConfirmOpen(false)}>×</button>
            </div>
            <div className="panel-body">
              <p style={{ marginBottom: 12 }}>Move <strong>{selAr.length} AR</strong> and <strong>{selAp.length} AP</strong> from <strong>{source.po_number}</strong> to <strong>{target.po_number}</strong>.</p>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label>Type the source PO number <strong>{source.po_number}</strong> to confirm</label>
                <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={source.po_number} style={{ fontFamily: 'monospace' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => setConfirmOpen(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ background: 'var(--danger-text)', opacity: confirmText.trim() === source.po_number ? 1 : 0.5 }}
                  disabled={busy || confirmText.trim() !== source.po_number} onClick={doMove}>
                  {busy ? 'Moving…' : 'Move invoices'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
